import { useUiStrings } from "@seagames/ui";
import { type ReactNode, type SyntheticEvent, useEffect, useId, useRef } from "react";
import { CloseIcon } from "../transport/icons";

/**
 * A side panel over the page: a native modal `<dialog>` (top layer, focus
 * trapped, Esc closes, focus returns to the opener — the browser's job, as
 * in `@seagames/ui`'s Modal), drawn as a drawer on the right from 40rem up
 * and as a bottom sheet on phones (`.ent-side` in entries-play.css).
 */
export function SidePanel({
	open,
	onClose,
	title,
	subtitle,
	headerExtra,
	footer,
	children,
}: {
	open: boolean;
	onClose: () => void;
	title: ReactNode;
	subtitle?: ReactNode;
	/** Under the title row (the sport's "interested" switch). */
	headerExtra?: ReactNode;
	footer?: ReactNode;
	children: ReactNode;
}) {
	const strings = useUiStrings().modal;
	const ref = useRef<HTMLDialogElement>(null);
	const titleId = useId();
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
			el.querySelector<HTMLElement>("[data-autofocus]")?.focus();
		} else if (!open && el.open) el.close();
	}, [open]);

	const onCancel = (e: SyntheticEvent<HTMLDialogElement>) => {
		e.preventDefault();
		onCloseRef.current();
	};

	return (
		<dialog
			ref={ref}
			className="ent-side"
			aria-labelledby={titleId}
			onCancel={onCancel}
			onClick={(e) => {
				if (e.target === e.currentTarget) onCloseRef.current();
			}}
		>
			{open && (
				<div className="ent-side__box">
					<header className="ent-side__head">
						<div className="min-w-0 flex-1">
							<h2 id={titleId} className="text-[1.0625rem] leading-tight font-bold text-gms-navy-ink">
								{title}
							</h2>
							{subtitle && <p className="mt-[0.125rem] text-[0.75rem] text-gms-ink-3">{subtitle}</p>}
						</div>
						<button type="button" className="ent-side__x" aria-label={strings.close} onClick={onClose}>
							<CloseIcon className="size-[1.125rem]" />
						</button>
					</header>
					{headerExtra && <div className="ent-side__extra">{headerExtra}</div>}
					<div className="ent-side__body">{children}</div>
					{footer && <footer className="ent-side__foot">{footer}</footer>}
				</div>
			)}
		</dialog>
	);
}
